import { default as endpoints } from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import CategoryOptionLabel from '@app/src/components/CategoryOptionLabel'
import LinkButton from '@app/src/components/LinkButton'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import { useGetFilterLabels } from '@app/src/hooks/getFilterLabels'
import { useStringifyQueryFilters } from '@app/src/hooks/queryState'
import { RequestAutomationType } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/RequestAutomationType'
import { CONFIGURATION_PAGE_IDS } from '@app/src/pages/Configurations/ConfigurationsScene'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { RequestAutomation } from '@app/src/types/automations'
import type { Category } from '@app/src/types/categories'
import { ONE_MINUTE_MS, Roles } from '@app/src/wf-constants'
import paths from '@app/src/wf-constants/paths'
import type { SxProps, Theme } from '@mui/material'
import { Alert, AlertTitle, Box, Skeleton, Stack, Typography } from '@mui/material'
import { capitalize } from 'lodash'
import React from 'react'
import { useIntl } from 'react-intl'
import { generatePath } from 'react-router'

const CATEGORY_OPTIONS_NAME = 'categoryOptions.id'

const flattenAndMerge = (arr: FilterGroup[][]): { name: string; value: string[] }[] => {
  const result: Record<string, Set<string>> = {}

  arr.flat().forEach(item => {
    if (!result[item.name]) {
      result[item.name] = new Set()
    }
    item.filters.forEach(filter => {
      ;(Array.isArray(filter.value) ? filter.value : []).forEach(value => result[item.name].add(String(value)))
    })
  })

  return Object.entries(result).map(([name, values]) => ({
    name,
    value: Array.from(values),
  }))
}

interface RequestAutomationWarningAlertProps {
  automationType: RequestAutomationType
  providerType?: string
  currentCategory?: Category
  isCustomCategoryOption?: boolean
  closeDrawer?: () => void
  sx?: SxProps<Theme>
}

const AlertLoader = ({ canGoToAutomation }: { canGoToAutomation: boolean }) => {
  return (
    <Stack my={-2}>
      <Skeleton width={350} height={50} />
      {canGoToAutomation && <Skeleton width={100} height={50} />}
    </Stack>
  )
}

const baseAlertStyle = {
  '& .MuiAlert-icon': {
    alignSelf: 'flex-start',
    marginLeft: 0,
  },
  '& .MuiAlert-message': {
    overflowY: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  '& .MuiAlert-action': {
    alignSelf: 'flex-end',
    marginTop: 1,
  },
  padding: '16px',
}

const RequestAutomationWarningAlert: React.FC<RequestAutomationWarningAlertProps> = ({
  automationType,
  providerType,
  currentCategory,
  isCustomCategoryOption,
  closeDrawer,
  sx,
}) => {
  const { stringifyQueryFilters } = useStringifyQueryFilters()
  const { formatMessage } = useIntl()
  const { role } = useAuthentication().scope
  const canGoToAutomation = role === Roles.Admin || role === Roles.WfAdmin
  const { getFilterLabel } = useGetFilterLabels({})

  const automationRedirectUrl = stringifyQueryFilters({
    url: generatePath(paths.configurations, { configurationsPage: CONFIGURATION_PAGE_IDS.Automations }),
    queryParams: {},
  })

  const { items: automations, isLoading: isAutomationsLoading } = useFetchCollectionWithPost<RequestAutomation>({
    payload: {
      filter: [
        {
          name: 'isActive',
          filters: [
            {
              value: true,
              operator: Operators.EqualTo,
            },
          ],
        },
      ],
      include: [],
    },
    key: FetchKey.Automation,
    options: { staleTime: ONE_MINUTE_MS, gcTime: ONE_MINUTE_MS * 10 },
    endpoint: endpoints.automations,
  })

  const hasAutomationForNewProvider = Boolean(
    automations.find(automation => automation.automationType === RequestAutomationType.FOR_EVERY_NEW_PROVIDER),
  )

  const hasAutomationForCategory = Boolean(
    automations.find(automation => automation.automationType === RequestAutomationType.FOR_PROVIDERS_IN_CATEGORY),
  )

  const hasAutomationForAiEnrich = Boolean(
    automations.find(automation => automation.automationType === RequestAutomationType.AI_ENRICH),
  )

  const hasAutomationForAiScreen = Boolean(
    automations.find(automation => automation.automationType === RequestAutomationType.AI_SCREEN),
  )

  const automationsFilters = automations.map(automation => automation.categoryFilters ?? [])

  const flattenedAutomationFilters = flattenAndMerge(automationsFilters)

  const propertyNames = flattenedAutomationFilters
    .filter(filter => filter.name !== CATEGORY_OPTIONS_NAME)
    ?.filter(filter => filter.name !== 'country.id')
    ?.filter(filter => filter.name !== 'mappingNodes.actorType.id')
    ?.map(filters => formatMessage({ id: `dashboard.sourcing.companyStatistics.${filters.name}` }))

  const categoryOptionsFilter = flattenedAutomationFilters.find(filter => filter.name === CATEGORY_OPTIONS_NAME)

  const { data: categories, isLoading: isCategoriesLoading } = useFetchCollectionWithPost<Category>({
    key: FetchKey.Category,
    endpoint: endpoints.categoryGetWithIds,
    payload: {
      filter: [
        {
          name: CATEGORY_OPTIONS_NAME,
          filters: [
            {
              value: categoryOptionsFilter?.value,
              operator: Operators.In,
            },
          ],
        },
      ],
      include: [],
    },
    options: {
      enabled: Boolean(categoryOptionsFilter),
    },
  })

  const currentCategoryValues = flattenedAutomationFilters.find(
    category => category.name === (isCustomCategoryOption ? CATEGORY_OPTIONS_NAME : currentCategory?.name),
  )?.value

  const categoryNames = categories?.items?.map(category => category.name)

  const isLoading = isAutomationsLoading || isCategoriesLoading

  const alertStyle = { ...baseAlertStyle, ...sx }

  const getCategoryOptionAutomationWarningText = (currentCategoryValues: string[]) => {
    const maxCategoryOptionsToShow = 7
    if (currentCategoryValues.length > maxCategoryOptionsToShow)
      return formatMessage({ id: 'schemas.provider.requestAutomationCustomCategoriesWarningMultipleOptions' })

    return formatMessage(
      { id: 'schemas.provider.requestAutomationCustomCategoriesWarning' },
      {
        categories: currentCategoryValues.map((v, i) => (
          <CategoryOptionLabel
            key={v}
            categoryOptionId={String(v)}
            categoryCount={currentCategoryValues.length}
            isLastItem={i === currentCategoryValues.length - 1}
          />
        )),
      },
    )
  }

  if (isCustomCategoryOption) {
    return (
      <>
        {currentCategoryValues ? (
          <Alert severity="info" sx={alertStyle}>
            {isLoading ? (
              <AlertLoader canGoToAutomation={canGoToAutomation} />
            ) : (
              <>
                <AlertTitle>{getCategoryOptionAutomationWarningText(currentCategoryValues)}</AlertTitle>
                {canGoToAutomation && (
                  <Box display="flex" justifyContent="flex-end" mt={1}>
                    <LinkButton to={automationRedirectUrl ? automationRedirectUrl : ''} onClick={closeDrawer}>
                      <Typography variant="button">{formatMessage({ id: 'import.viewAutomation' })}</Typography>
                    </LinkButton>
                  </Box>
                )}
              </>
            )}
          </Alert>
        ) : null}
      </>
    )
  }

  if (currentCategory) {
    return (
      <>
        {currentCategoryValues ? (
          <Alert severity="info" sx={alertStyle}>
            {isLoading ? (
              <AlertLoader canGoToAutomation={canGoToAutomation} />
            ) : (
              <>
                <AlertTitle>
                  {formatMessage(
                    { id: 'schemas.provider.requestAutomationCustomCategoriesWarning' },
                    { categories: getFilterLabel(currentCategory.name, currentCategoryValues) },
                  )}
                </AlertTitle>
                {canGoToAutomation && (
                  <Box display="flex" justifyContent="flex-end" mt={1}>
                    <LinkButton to={automationRedirectUrl ? automationRedirectUrl : ''} onClick={closeDrawer}>
                      <Typography variant="button">{formatMessage({ id: 'import.viewAutomation' })}</Typography>
                    </LinkButton>
                  </Box>
                )}
              </>
            )}
          </Alert>
        ) : null}
      </>
    )
  }

  if (hasAutomationForNewProvider && automationType === RequestAutomationType.FOR_EVERY_NEW_PROVIDER) {
    return (
      <Alert severity="info" sx={alertStyle}>
        {isLoading ? (
          <AlertLoader canGoToAutomation={canGoToAutomation} />
        ) : (
          <>
            {formatMessage(
              { id: 'resourceCollections.create.requestAutomationWarning' },
              { provider: formatMessage({ id: `general.${capitalize(providerType)}` }, { count: 1 }) },
            )}
            {canGoToAutomation && (
              <Box display="flex" justifyContent="flex-end" mt={1}>
                <LinkButton to={automationRedirectUrl ? automationRedirectUrl : ''} onClick={closeDrawer}>
                  <Typography variant="button">{formatMessage({ id: 'import.viewAutomation' })}</Typography>
                </LinkButton>
              </Box>
            )}
          </>
        )}
      </Alert>
    )
  }

  if (hasAutomationForCategory && automationType === RequestAutomationType.FOR_PROVIDERS_IN_CATEGORY) {
    return (
      <Alert severity="info" sx={alertStyle}>
        {isLoading ? (
          <AlertLoader canGoToAutomation={canGoToAutomation} />
        ) : (
          <>
            {formatMessage(
              { id: 'schemas.provider.requestAutomationCategoriesWarning' },
              { categories: [...propertyNames, ...(categoryNames ?? [])].join(', ') },
            )}
            {canGoToAutomation && (
              <Box display="flex" justifyContent="flex-end" mt={1}>
                <LinkButton to={automationRedirectUrl ? automationRedirectUrl : ''} onClick={closeDrawer}>
                  <Typography variant="button">{formatMessage({ id: 'import.viewAutomation' })}</Typography>
                </LinkButton>
              </Box>
            )}
          </>
        )}
      </Alert>
    )
  }

  if (hasAutomationForAiEnrich && automationType === RequestAutomationType.AI_ENRICH) {
    return (
      <Alert severity="info" sx={alertStyle}>
        {isLoading ? (
          <AlertLoader canGoToAutomation={canGoToAutomation} />
        ) : (
          <>
            {formatMessage(
              { id: 'resourceCollections.create.aiEnrichAutomationWarning' },
              { provider: formatMessage({ id: `general.${capitalize(providerType)}` }, { count: 1 }) },
            )}
            {canGoToAutomation && (
              <Box display="flex" justifyContent="flex-end" mt={1}>
                <LinkButton to={automationRedirectUrl ? automationRedirectUrl : ''} onClick={closeDrawer}>
                  <Typography variant="button">{formatMessage({ id: 'import.viewAutomation' })}</Typography>
                </LinkButton>
              </Box>
            )}
          </>
        )}
      </Alert>
    )
  }

  if (hasAutomationForAiScreen && automationType === RequestAutomationType.AI_SCREEN) {
    return (
      <Alert severity="info" sx={alertStyle}>
        {isLoading ? (
          <AlertLoader canGoToAutomation={canGoToAutomation} />
        ) : (
          <>
            {formatMessage(
              { id: 'resourceCollections.create.aiScreenAutomationWarning' },
              { provider: formatMessage({ id: `general.${capitalize(providerType)}` }, { count: 1 }) },
            )}
            {canGoToAutomation && (
              <Box display="flex" justifyContent="flex-end" mt={1}>
                <LinkButton to={automationRedirectUrl ? automationRedirectUrl : ''} onClick={closeDrawer}>
                  <Typography variant="button">{formatMessage({ id: 'import.viewAutomation' })}</Typography>
                </LinkButton>
              </Box>
            )}
          </>
        )}
      </Alert>
    )
  }

  return null
}

export default RequestAutomationWarningAlert
